import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Filter } from "lucide-react";
import { downloadCSV, ORANGE, KpiCard, FunnelRow } from "./utils";

export default function InquiryConversionReport() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/reports/inquiry-conversion", { params: { period } })
      .then(({ data }) => setData(data))
      .catch((e) => { setData(null); toast.error(formatApiError(e)); })
      .finally(() => setLoading(false));
  }, [period]);

  const totals = data?.totals || {};
  const rows = useMemo(() => {
    const all = data?.rows || [];
    if (!q.trim()) return all;
    const s = q.trim().toLowerCase();
    return all.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(s) ||
      (r.product_name || "").toLowerCase().includes(s) ||
      (r.job_number || "").toLowerCase().includes(s)
    );
  }, [data, q]);

  const exportCsv = () => {
    downloadCSV(
      "inquiry_conversion",
      ["Job #", "Customer", "Product", "Inquiry Type", "Order Type", "Status", "Converted", "Created", "Dispatch"],
      rows.map((r) => [
        r.job_number, r.customer_name, r.product_name, r.inquiry_type, r.order_type,
        r.status, r.converted ? "Yes" : "No",
        (r.created_at || "").slice(0, 10), (r.dispatch_date || "").slice(0, 10),
      ])
    );
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <span className="label-caps text-neutral-600">Period</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="inq-period" className="w-40 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="quarter">This quarter</SelectItem>
            <SelectItem value="year">This year</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
          <Input
            data-testid="inq-search"
            placeholder="Filter customer / product / job…"
            className="pl-8 rounded-sm w-72"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} className="rounded-sm" data-testid="inq-export">
            Download .csv
          </Button>
        </div>
      </div>

      {/* Funnel KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Total Inquiries" value={totals.total_inquiries} testId="inq-kpi-total" />
        <KpiCard label="New Inquiries" value={totals.new_inquiries} testId="inq-kpi-new" />
        <KpiCard label="Repeat Inquiries" value={totals.repeat_inquiries} testId="inq-kpi-repeat" />
        <KpiCard
          label="Overall Conversion"
          value={`${totals.conversion_pct_overall ?? 0}%`}
          hint={`${totals.converted_total ?? 0} of ${totals.total_inquiries ?? 0}`}
          accent={ORANGE}
          testId="inq-kpi-conv"
        />
      </div>

      {/* Funnel breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <FunnelRow
          label="New Inquiries → Orders"
          num={totals.converted_new || 0}
          den={totals.new_inquiries || 0}
          pct={totals.conversion_pct_new || 0}
          orders={totals.orders_new || 0}
          orderLabel="New Orders"
        />
        <FunnelRow
          label="Repeat Inquiries → Orders"
          num={totals.converted_repeat || 0}
          den={totals.repeat_inquiries || 0}
          pct={totals.conversion_pct_repeat || 0}
          orders={totals.orders_repeat || 0}
          orderLabel="Repeat Orders"
        />
      </div>

      {/* Detail table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="inq-detail-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">Job #</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Product</th>
              <th className="text-left px-4 py-2.5">Inquiry Type</th>
              <th className="text-left px-4 py-2.5">Order Type</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-center px-4 py-2.5">Converted</th>
              <th className="text-left px-4 py-2.5">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 500).map((r, i) => (
              <tr key={`${r.job_number}-${i}`} className="border-t border-neutral-100 data-row">
                <td className="px-4 py-2.5 font-mono-num text-xs">{r.job_number}</td>
                <td className="px-4 py-2.5">{r.customer_name}</td>
                <td className="px-4 py-2.5 text-neutral-600 max-w-xs truncate" title={r.product_name}>{r.product_name}</td>
                <td className="px-4 py-2.5 text-xs">
                  <span className={`px-1.5 py-0.5 rounded-sm border ${
                    r.inquiry_type === "New Inquiry" ? "bg-blue-50 text-blue-900 border-blue-200" :
                    r.inquiry_type === "Repeat Inquiry" ? "bg-violet-50 text-violet-900 border-violet-200" :
                    "bg-neutral-50 text-neutral-500 border-neutral-200"
                  }`}>{r.inquiry_type}</span>
                </td>
                <td className="px-4 py-2.5 text-xs text-neutral-600">{r.order_type}</td>
                <td className="px-4 py-2.5 text-xs text-neutral-600">{(r.status || "").replace(/_/g, " ")}</td>
                <td className="px-4 py-2.5 text-center">
                  {r.converted
                    ? <span className="inline-block px-1.5 py-0.5 text-[10px] rounded-sm bg-emerald-100 text-emerald-900 border border-emerald-300">Yes</span>
                    : <span className="inline-block px-1.5 py-0.5 text-[10px] rounded-sm bg-neutral-100 text-neutral-500 border border-neutral-200">No</span>}
                </td>
                <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-500">{(r.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">No inquiries in this period.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">
                <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 bg-[#FF5A00] rounded-full animate-pulse" />Loading inquiry funnel…</span>
              </td></tr>
            )}
            {rows.length > 500 && (
              <tr><td colSpan={8} className="px-4 py-2 text-center text-xs text-neutral-400">+ {rows.length - 500} more rows in CSV export…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

